import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Box, Button, MenuItem, Stack, Switch, TextField, ToggleButton, ToggleButtonGroup, Tooltip, Typography } from '@mui/material';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import { collection, doc, getDocs, limit, onSnapshot, query, serverTimestamp, setDoc, Timestamp, where } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { useSearchParams } from 'react-router-dom';
import { db, functions } from '../../firebase';
import { useAuth } from '../../AuthContext';
import AutoReplyMessagesSection from './AutoReplyMessagesSection';
import { FlowModeControl } from './FlowModeControl';
import CustomerFlowCanvas from './CustomerFlowCanvas';
import HubJourneyTools from './HubJourneyTools';
import { OldItemRow, useOldAutomation } from './oldAutomation';
import type { OldAutomationApi, OldItem } from './oldAutomation';
import { StatusPill, UI, denseInputSx, primaryButtonSx, textButtonSx } from './settingsUi';
import {
  CategoryTag,
  TemplateStatusChip,
  WhatsappPhonePreview,
  approvalSummary,
  templateIssues,
} from './hubTemplates';
import { useConfirmAction } from '../ConfirmActionDialog';
import type { TemplateStatusesApi } from './hubTemplates';
import { AUTO_REPLY_FLOWS, AUTO_REPLY_LANGS } from '../../utils/whatsappAutoReplyFlows';
import type { AutoReplyLang } from '../../utils/whatsappAutoReplyFlows';
import {
  DEFAULT_AUTO_REPLY_SETTINGS,
  WHATSAPP_AUTO_REPLY_DOC,
  WHATSAPP_SETTINGS_COLLECTION,
  isFlowEnabled,
  getFlowMode,
  normalizeAutoReplySettings,
  toPhone10,
} from '../../utils/whatsappWorkingHours';
import type { FlowMode, WhatsappAutoReplySettings } from '../../utils/whatsappWorkingHours';
import { JOURNEY_AUTOMATIONS, JOURNEY_STAGES, isJourneyBuilt } from '../../utils/whatsappJourney';
import type { JourneyAutomation } from '../../utils/whatsappJourney';
import { TEMPLATE_DRAFTS, TEMPLATE_LANG_CODES, templateIssues as draftTemplateIssues, templatePreviewText } from '../../utils/whatsappTemplateDrafts';

const CONVERSATION_FLOWS = AUTO_REPLY_FLOWS.filter((f) => f.key !== 'shared');
const FLOW_STAGE_KEYS: Record<string, string[]> = {
  enquiry: ['projectExplore', 'newContact', 'unknownContact', 'afterHours', 'busyNote'],
  followup: ['brochure', 'callback'],
  visit: ['siteVisit'],
  afterVisit: [],
  booking: [],
  afterSale: ['existingCustomer'],
};

const switchSx = {
  '& .MuiSwitch-switchBase.Mui-checked': { color: UI.gold },
  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: UI.gold },
};

const GroupLabel = ({ children }: { children: React.ReactNode }) => (
  <Typography sx={{ fontSize: 10.5, fontWeight: 700, letterSpacing: 0.8, color: UI.muted, px: 1.5, pt: 1.5, pb: 0.5 }}>{children}</Typography>
);

const ListRow = ({
  title,
  sub,
  warning,
  selected,
  onClick,
  right,
}: {
  title: string;
  sub: string;
  /** Shown in red under the row, e.g. a template that cannot send. */
  warning?: string;
  selected: boolean;
  onClick: () => void;
  right: React.ReactNode;
}) => (
  <Stack
    direction="row"
    alignItems="center"
    gap={1}
    role="button"
    tabIndex={0}
    aria-pressed={selected}
    onClick={onClick}
    onKeyDown={(e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onClick();
      }
    }}
    sx={{
      px: 1.5,
      py: 0.9,
      cursor: 'pointer',
      bgcolor: selected ? UI.goldTint : 'transparent',
      boxShadow: selected ? `inset 0 0 0 1px ${UI.gold}55` : 'none',
      borderRadius: 1.25,
      mx: 0.5,
      '&:hover': { bgcolor: selected ? UI.goldTint : UI.canvas },
      '&:focus-visible': { outline: `2px solid ${UI.gold}`, outlineOffset: -2 },
    }}
  >
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography sx={{ fontSize: 13, fontWeight: selected ? 600 : 500, color: UI.ink, lineHeight: 1.35 }}>{title}</Typography>
      <Typography sx={{ fontSize: 11.5, color: UI.muted }}>{sub}</Typography>
      {warning && <Typography sx={{ fontSize: 11.5, color: '#b3261e', fontWeight: 600, lineHeight: 1.35, mt: 0.25 }}>⚠ {warning}</Typography>}
    </Box>
    {right}
  </Stack>
);

interface JourneyDetailProps {
  automation: JourneyAutomation;
  templates: TemplateStatusesApi;
  mode: FlowMode;
  onModeChange: (mode: FlowMode) => void;
  switchedOn: { at: Date | null; by: string | null };
  stats: { sent: number; skipped: Record<string, number> };
  oldItems: OldItem[];
  oldApi: OldAutomationApi;
  /** Conversation flows answering this automation's buttons that are off. */
  offDependencies: string[];
  onOpenTemplates: () => void;
}

const SKIP_REASON_LABEL: Record<string, string> = {
  'automation-off': 'Automation was off',
  'scheduled-before-switch-on': 'Visit scheduled before switch-on',
  'confirmation-just-sent': 'Confirmation had just gone out',
  'lead-missing': 'Lead deleted',
  'no-phone': 'No valid mobile number',
  'not-allowed-by-master-switch': 'Previously blocked by the shared mode or test-number list',
  'not-allowed-by-flow-mode': 'This flow is in Test and the contact is not a test number, or automation is paused',
  'opted-out': 'Contact opted out',
  'template-not-approved': 'Template not approved yet',
  'no-site-location': 'No site latitude/longitude in Website CMS',
  'no-settings': 'Settings not saved yet',
  'quiet-hours': 'Quiet hours (moved to 10 AM)',
  'marketing-sent-recently': 'Marketing message sent in the last 3 days',
  'already-chatting-on-whatsapp': 'Customer was already chatting on WhatsApp',
  'created-before-switch-on': 'Lead created before switch-on',
  'already-sent-today': 'Already sent to this lead today',
  'already-in-touch': 'Customer replied or the team was in touch',
  'recent-automated-message': 'Another automated WhatsApp went out in the last 24 hours',
  'completed-before-switch-on': 'Visit completed before switch-on',
  'booked-before-switch-on': 'Booking made before switch-on',
  'kyc-already-complete': 'KYC already uploaded',
  'paid-after-demand': 'Payment received after the demand',
  'demand-before-switch-on': 'Demand created before switch-on',
  'window-passed': 'Reminder time had passed',
  'booking-missing': 'Booking not found',
  'receipt-before-switch-on': 'Receipt recorded before switch-on',
  'source-not-in-welcome-list': 'Lead source not set for the welcome (walk-in, call, manual...)',
  'broker-lead': 'Broker-referred lead',
  'career-application': 'Job application, not a property enquiry',
  'welcomed-in-last-30-days': 'Same number got a welcome in the last 30 days',
  'lead-closed': 'Lead is lost, closed or booked',
  'max-3-per-lead': 'Already sent 3 times to this lead',
  'sent-in-last-3-days': 'Sent to this lead in the last 3 days',
  'customer-replied-before': 'Customer replied to an earlier one',
  'customer-active-on-whatsapp': 'Customer is chatting on WhatsApp: reply in Messages',
  'message-sent-in-last-3-hours': 'Another automated message went out in the last 3 hours',
  'call-logged-late': 'Call logged more than 2 hours later',
  'held-until-10am': 'Logged at night, held until 10 AM',
  'wrong-number-stopped': 'Wrong number: automated WhatsApp stopped',
};

/**
 * Quick-reply payloads per template, same as the live senders. {pid} = website
 * project, {lead} = CRM lead (taps act on that lead, e.g. reschedule).
 */
const TEST_BUTTONS: Record<string, string[]> = {
  swarna_enquiry_received: ['ar:brochure:{pid}', 'ar:visit:{pid}', 'ar:explore:{pid}'],
  swarna_enquiry_choose_project: ['ar:projects:-'],
  swarna_call_attempt: ['ar:vcall:{lead}', 'ar:callback:{pid}', 'ar:chat:{lead}'],
  swarna_enquiry_followup: ['ar:visit:{pid}', 'ar:brochure:{pid}', 'ar:optout:-'],
  swarna_enquiry_nudge: ['ar:visit:{pid}', 'ar:brochure:{pid}', 'ar:optout:-'],
  swarna_cold_lead: ['ar:brochure:{pid}', 'ar:visit:{pid}', 'ar:optout:-'],
  swarna_visit_confirmed: ['ar:vreschedule:{lead}', 'ar:vcancel:{lead}'],
  swarna_visit_reminder_day: ['ar:vconfirm:{lead}', 'ar:vreschedule:{lead}'],
  swarna_visit_reminder_soon: ['ar:vlate:{lead}', 'ar:vreschedule:{lead}'],
  swarna_visit_missed: ['ar:vnewtime:{lead}', 'ar:vcall:{lead}'],
  swarna_visit_thanks: ['ar:vtloved:{lead}', 'ar:vtmore:{lead}', 'ar:vtno:{lead}'],
  swarna_unit_details: ['ar:visit:{pid}', 'ar:callback:{pid}'],
  swarna_booking_documents: ['ar:docq:test'],
  swarna_payment_due: ['ar:paid:test', 'ar:accounts:test'],
  swarna_possession_ready: ['ar:handover:test', 'ar:bcall:test'],
  swarna_new_launch: ['ar:brochure:{pid}', 'ar:visit:{pid}', 'ar:optout:-'],
  swarna_birthday: ['ar:optout:-'],
  swarna_festival_greeting: ['ar:optout:-'],
  swarna_collection_ready: ['ar:optout:-'],
};

type TestProject = { id: string; name: string; location: string; latitude: number | null; longitude: number | null };

const projectCoords = (p: any): { latitude: number; longitude: number } | null => {
  const lat = Number(p?.latitude);
  const lng = Number(p?.longitude);
  if (p?.latitude !== null && p?.latitude !== undefined && p?.latitude !== '' && Number.isFinite(lat) && Number.isFinite(lng)) {
    return { latitude: lat, longitude: lng };
  }
  const url = String(p?.mapEmbedUrl || '');
  const a = url.match(/!3d(-?\d+(?:\.\d+)?)!2d(-?\d+(?:\.\d+)?)/);
  if (a) return { latitude: Number(a[1]), longitude: Number(a[2]) };
  const b = url.match(/!2d(-?\d+(?:\.\d+)?)!3d(-?\d+(?:\.\d+)?)/);
  if (b) return { latitude: Number(b[2]), longitude: Number(b[1]) };
  return null;
};

const JourneyDetail = ({ automation, templates, mode, onModeChange, switchedOn, stats, oldItems, oldApi, offDependencies, onOpenTemplates }: JourneyDetailProps) => {
  const on = mode !== 'off';
  const built = isJourneyBuilt(automation);
  const skippedTotal = Object.values(stats.skipped).reduce((a, b) => a + b, 0);
  const [lang, setLang] = useState<AutoReplyLang>('en');
  const [previewName, setPreviewName] = useState(automation.templates[0]);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [testPhone, setTestPhone] = useState('918000346605');
  const [leadSourcePhone, setLeadSourcePhone] = useState('918000346605');
  const [testLeads, setTestLeads] = useState<Array<{ id: string; name: string; createdAtMs: number; exec: string }>>([]);
  const [selectedLeadId, setSelectedLeadId] = useState('');
  const [leadLookupBusy, setLeadLookupBusy] = useState(false);
  const [leadLookupError, setLeadLookupError] = useState('');
  const [testProjectId, setTestProjectId] = useState('');
  const [testProjects, setTestProjects] = useState<TestProject[]>([]);
  const [testValues, setTestValues] = useState<Record<number, string>>({});
  const [testBusy, setTestBusy] = useState(false);
  const [testResult, setTestResult] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  useEffect(() => {
    setTestValues({});
  }, [previewName, selectedLeadId, testProjectId]);
  useEffect(() => {
    setPreviewName(automation.templates[0]);
    setSubmitError(null);
    setTestResult(null);
  }, [automation]);
  useEffect(() => {
    if (!built) return;
    let active = true;
    getDocs(collection(db, 'website_projects'))
      .then((snap) => {
        if (!active) return;
        const projects = snap.docs.filter((d) => d.data().isPublished !== false && d.data().name).map((d) => {
          const c = projectCoords(d.data());
          return {
            id: d.id,
            name: String(d.data().name || ''),
            location: String(d.data().location || ''),
            latitude: c?.latitude ?? null,
            longitude: c?.longitude ?? null,
          };
        });
        setTestProjects(projects);
        setTestProjectId((current) => current || projects[0]?.id || '');
      })
      .catch(() => { if (active) setTestProjects([]); });
    return () => { active = false; };
  }, [automation.key, built]);
  useEffect(() => {
    if (!built) return;
    const digits = leadSourcePhone.replace(/\D/g, '');
    if (!/^\d{10}$/.test(digits) && !/^91\d{10}$/.test(digits)) {
      setTestLeads([]);
      setSelectedLeadId('');
      setLeadLookupError('Enter the CRM lead’s 10-digit mobile number.');
      setLeadLookupBusy(false);
      return;
    }
    let active = true;
    setLeadLookupBusy(true);
    setLeadLookupError('');
    getDocs(query(collection(db, 'leads'), where('phone10', '==', digits.slice(-10)), limit(20)))
      .then((snap) => {
        if (!active) return;
        const leads = snap.docs.map((d) => ({
          id: d.id,
          name: String(d.data().name || '').trim(),
          createdAtMs: d.data().createdAt?.toMillis?.() || 0,
          exec: String(d.data().assignedToName || '').trim(),
        })).filter((lead) => lead.name).sort((a, b) => b.createdAtMs - a.createdAtMs);
        setTestLeads(leads);
        setSelectedLeadId((current) => leads.some((lead) => lead.id === current) ? current : leads[0]?.id || '');
        setLeadLookupError(leads.length ? '' : 'No CRM lead with a name was found for this mobile number.');
      })
      .catch((err: any) => {
        if (!active) return;
        setTestLeads([]);
        setSelectedLeadId('');
        setLeadLookupError(`Could not look up the CRM lead: ${err?.message || err}`);
      })
      .finally(() => { if (active) setLeadLookupBusy(false); });
    return () => { active = false; };
  }, [automation.key, built, leadSourcePhone]);
  const drafts = automation.templates.map((name) => templates.effective(name)).filter(Boolean) as typeof TEMPLATE_DRAFTS;
  const preview = drafts.find((d) => d.name === previewName) || drafts[0];
  const approvedTestTemplate = preview ? templates.versionsFor(preview.name, lang).sending : null;
  const testButtons = (preview && TEST_BUTTONS[preview.name]) || [];
  const needsProject = !!preview && (preview.variables.some((v) => /project/i.test(v.label)) || testButtons.some((b) => b.includes('{pid}')) || preview.header?.type === 'LOCATION');
  const selectedTestProject = testProjects.find((item) => item.id === testProjectId);
  const selectedTestLead = testLeads.find((lead) => lead.id === selectedLeadId);
  const testLeadFirstName = selectedTestLead?.name.split(/\s+/)[0] || '';
  const defaultValue = (label: string, example: string) => {
    if (/customer name/i.test(label)) return testLeadFirstName || example;
    if (/^project$/i.test(label)) return selectedTestProject?.name || example;
    if (/executive/i.test(label)) return selectedTestLead?.exec.split(/\s+/)[0] || example;
    if (/site address/i.test(label)) return selectedTestProject?.location || example;
    return example;
  };
  const testValuesList = (preview?.variables || []).map((v, i) => testValues[i] ?? defaultValue(v.label, v.example));
  const previewBody = preview?.body[lang].replace(/\{\{(\d+)\}\}/g, (match, index) => testValuesList[Number(index) - 1] || match);

  const sendJourneyTest = async () => {
    if (!preview || !approvedTestTemplate) return;
    const digits = testPhone.replace(/\D/g, '');
    if (!/^\d{10}$/.test(digits) && !/^91\d{10}$/.test(digits)) {
      setTestResult({ type: 'error', text: 'Enter a 10-digit Indian mobile number, with optional +91.' });
      return;
    }
    const project = selectedTestProject;
    if (needsProject && !project) {
      setTestResult({ type: 'error', text: 'Select a project for this template.' });
      return;
    }
    if (testValuesList.some((v) => !String(v || '').trim())) {
      setTestResult({ type: 'error', text: 'Fill every value before sending.' });
      return;
    }
    if (testButtons.some((b) => b.includes('{lead}')) && !selectedLeadId) {
      setTestResult({ type: 'error', text: 'Choose a CRM lead: this template’s buttons act on a lead.' });
      return;
    }
    let brochureUrl = '';
    let projectSlug = '';
    if (preview.header?.type === 'DOCUMENT') {
      try {
        const projectSnap = await getDocs(collection(db, 'projects'));
        const crmProject = projectSnap.docs.map((d) => d.data()).find((p) =>
          String(p.name || '').trim().toLowerCase() === String(project?.name || '').trim().toLowerCase());
        brochureUrl = String(crmProject?.brochureUrl || '').trim();
        projectSlug = String(crmProject?.websiteSlug || '').trim();
      } catch (err: any) {
        setTestResult({ type: 'error', text: `Could not load the project brochure: ${err?.message || err}` });
        return;
      }
      if (!/^https?:\/\//i.test(brochureUrl) || !projectSlug) {
        setTestResult({ type: 'error', text: 'This project needs a brochure PDF and website link in CRM before this template can be tested.' });
        return;
      }
    }
    const header = preview.header?.type === 'LOCATION' ?
      (project?.latitude !== null && project?.latitude !== undefined ? [{
        type: 'header',
        parameters: [{ type: 'location', location: {
          latitude: String(project.latitude), longitude: String(project.longitude),
          name: project.name, address: project.location || project.name,
        } }],
      }] : null) :
      preview.header?.type === 'DOCUMENT' ? [{
        type: 'header', parameters: [{ type: 'document', document: { link: brochureUrl, filename: 'Project Brochure.pdf' } }],
      }] : [];
    if (header === null) {
      setTestResult({ type: 'error', text: 'This template shows the site pin: add latitude/longitude to the project in Website CMS.' });
      return;
    }
    const phone10 = digits.slice(-10);
    const clean = (v: string) => v.replace(/[\n\t]+/g, ' ').replace(/ {4,}/g, ' ').trim();
    const components = [
      ...header,
      ...(testValuesList.length ? [{ type: 'body', parameters: testValuesList.map((value) => ({ type: 'text', text: clean(value) })) }] : []),
      ...(preview.urlButton ? [{ type: 'button', sub_type: 'url', index: '0', parameters: [{ type: 'text', text: preview.name === 'swarna_collection_ready' ? '21.1702%2C72.8311' : projectSlug }] }] : []),
      ...testButtons.map((payload, index) => ({
        type: 'button', sub_type: 'quick_reply', index: String(index + (preview.urlButton ? 1 : 0)),
        parameters: [{ type: 'payload', payload: payload.replace('{pid}', project?.id || '-').replace('{lead}', selectedLeadId || '-') }],
      })),
    ];
    setTestBusy(true);
    setTestResult(null);
    try {
      await httpsCallable(functions, 'whatsappSendMessage')({
        to: `91${phone10}`,
        text: previewBody || preview.name,
        templateName: approvedTestTemplate,
        templateLanguage: TEMPLATE_LANG_CODES[lang],
        templateComponents: components,
        ...(selectedLeadId ? { leadId: selectedLeadId } : {}),
        metadata: { autoReplyTest: true, flow: automation.key, template: preview.name },
      });
      setTestResult({ type: 'success', text: `Sent ${approvedTestTemplate} (${lang.toUpperCase()}) to +91 ${phone10}. Check delivery in Messages.` });
    } catch (err: any) {
      setTestResult({ type: 'error', text: `Test could not be sent: ${err?.message || err}` });
    } finally {
      setTestBusy(false);
    }
  };

  const submitMissing = async () => {
    setSubmitError(null);
    for (const draft of drafts) {
      for (const { key } of AUTO_REPLY_LANGS) {
        if (!templates.needsSubmit(draft.name, key)) continue;
        const issues = draftTemplateIssues(draft, key);
        if (issues.length) {
          setSubmitError(`${draft.name} (${key}): ${issues.join(' ')}`);
          return;
        }
        const error = await templates.submit(draft, key);
        if (error) {
          setSubmitError(`${draft.name} (${key}): ${error}`);
          return;
        }
      }
    }
  };

  const missing = drafts.some((d) =>
    AUTO_REPLY_LANGS.some(({ key }) => templates.needsSubmit(d.name, key))
  );

  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 1fr) 340px' }, gap: 3, p: 2.5 }}>
      {built && preview && (
        <Box sx={{ gridColumn: '1 / -1', border: `1px solid ${UI.line}`, borderRadius: 1.5, overflow: 'hidden' }}>
          <Stack direction="row" alignItems="center" gap={1} sx={{ px: 1.5, py: 1, borderBottom: `1px solid ${UI.line}`, bgcolor: UI.canvas }}>
            <Typography sx={{ fontSize: 12, fontWeight: 600, color: UI.ink, flex: 1 }}>Send a test</Typography>
            <Typography sx={{ fontSize: 11.5, color: UI.muted }}>Real template, to your number. Does not change Off, Test or Live</Typography>
          </Stack>
          <Box sx={{ p: 1.5 }}>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0, 1fr)) auto' }, gap: 1.25, alignItems: 'start' }}>
              <Box>
                <Typography sx={{ fontSize: 11.5, fontWeight: 600, color: UI.muted, mb: 0.5 }}>Template</Typography>
                <TextField select size="small" fullWidth value={preview.name}
                  onChange={(e) => { setPreviewName(e.target.value); setTestResult(null); }}
                  inputProps={{ 'aria-label': 'Template for test' }}
                  sx={{ ...denseInputSx, '& .MuiInputBase-root': { ...denseInputSx['& .MuiInputBase-root'], bgcolor: UI.surface } }}>
                  {drafts.map((d) => <MenuItem key={d.name} value={d.name} sx={{ fontSize: 12.5, fontFamily: UI.mono }}>{d.name}</MenuItem>)}
                </TextField>
                {needsProject && (
                  <TextField select size="small" fullWidth value={testProjectId}
                    SelectProps={{ displayEmpty: true, renderValue: (selected) => testProjects.find((p) => p.id === String(selected))?.name || 'Choose project' }}
                    onChange={(e) => { setTestProjectId(e.target.value); setTestResult(null); }}
                    inputProps={{ 'aria-label': 'Project for test' }}
                    sx={{ ...denseInputSx, mt: 0.75, '& .MuiInputBase-root': { ...denseInputSx['& .MuiInputBase-root'], bgcolor: UI.surface } }}>
                    {testProjects.map((project) => <MenuItem key={project.id} value={project.id} sx={{ fontSize: 13 }}>{project.name}</MenuItem>)}
                  </TextField>
                )}
              </Box>
              <Box>
                <Typography sx={{ fontSize: 11.5, fontWeight: 600, color: UI.muted, mb: 0.5 }}>Lead data from</Typography>
                <TextField size="small" fullWidth placeholder="CRM lead mobile" value={leadSourcePhone}
                  onChange={(e) => { setLeadSourcePhone(e.target.value); setTestResult(null); }}
                  inputProps={{ 'aria-label': 'CRM lead mobile for test values', inputMode: 'tel' }}
                  sx={{ ...denseInputSx, '& .MuiInputBase-root': { ...denseInputSx['& .MuiInputBase-root'], bgcolor: UI.surface } }} />
                {testLeads.length > 1 ? (
                  <TextField select size="small" fullWidth value={selectedLeadId}
                    onChange={(e) => { setSelectedLeadId(e.target.value); setTestResult(null); }}
                    inputProps={{ 'aria-label': 'Lead record' }}
                    sx={{ ...denseInputSx, mt: 0.75, '& .MuiInputBase-root': { ...denseInputSx['& .MuiInputBase-root'], bgcolor: UI.surface } }}>
                    {testLeads.map((lead) => <MenuItem key={lead.id} value={lead.id} sx={{ fontSize: 13 }}>{lead.name}</MenuItem>)}
                  </TextField>
                ) : (
                  <Typography sx={{ fontSize: 11.5, mt: 0.4, color: selectedTestLead ? UI.ink : UI.muted }}>
                    {leadLookupBusy ? 'Looking up lead…' : selectedTestLead ? `Using ${selectedTestLead.name}` : leadLookupError}
                  </Typography>
                )}
              </Box>
              <Box>
                <Typography sx={{ fontSize: 11.5, fontWeight: 600, color: UI.muted, mb: 0.5 }}>Send to</Typography>
                <TextField size="small" fullWidth placeholder="919876543210" value={testPhone}
                  onChange={(e) => { setTestPhone(e.target.value); setTestResult(null); }}
                  onKeyDown={(e) => { if (e.key === 'Enter') void sendJourneyTest(); }}
                  inputProps={{ 'aria-label': 'Test WhatsApp number', inputMode: 'tel' }}
                  sx={{ ...denseInputSx, '& .MuiInputBase-root': { ...denseInputSx['& .MuiInputBase-root'], bgcolor: UI.surface } }} />
              </Box>
              <Box sx={{ pt: { md: 2.6 } }}>
                <Button variant="contained" disableElevation size="small"
                  disabled={testBusy || leadLookupBusy || !testPhone.trim() || !approvedTestTemplate || (needsProject && !testProjectId)}
                  onClick={sendJourneyTest} sx={primaryButtonSx}>
                  {testBusy ? 'Sending…' : 'Send test'}
                </Button>
              </Box>
            </Box>
            {preview.variables.length > 0 && (
              <Box sx={{ mt: 1.25 }}>
                <Typography sx={{ fontSize: 11.5, fontWeight: 600, color: UI.muted, mb: 0.5 }}>Values in this test (filled from the lead and project; edit freely)</Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', md: 'repeat(3, minmax(0, 1fr))' }, gap: 1 }}>
                  {preview.variables.map((v, i) => (
                    <TextField key={`${preview.name}-${i}`} size="small" label={`{{${i + 1}}} ${v.label}`} value={testValuesList[i] || ''}
                      onChange={(e) => setTestValues((prev) => ({ ...prev, [i]: e.target.value }))}
                      InputLabelProps={{ shrink: true }}
                      sx={{ ...denseInputSx, '& .MuiInputBase-root': { ...denseInputSx['& .MuiInputBase-root'], bgcolor: UI.surface } }} />
                  ))}
                </Box>
              </Box>
            )}
            <Typography sx={{ fontSize: 11.5, color: UI.muted, mt: 1 }}>
              Sends the approved version{approvedTestTemplate && approvedTestTemplate !== preview.name ? ` (${approvedTestTemplate})` : ''} in the language selected on the preview.
              {testButtons.some((b) => b.includes('{lead}')) && ' Button taps act on the chosen CRM lead (for example Cancel visit cancels its appointment), so use your own test lead.'}
              {!approvedTestTemplate && ' Available once Meta approves this template in this language.'}
            </Typography>
            {testResult && <Alert severity={testResult.type} sx={{ mt: 1, fontSize: 12, py: 0 }}>{testResult.text}</Alert>}
          </Box>
        </Box>
      )}
      <Box sx={{ minWidth: 0 }}>
        <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
          <Typography sx={{ fontSize: 16, fontWeight: 600, color: UI.ink }}>{automation.title}</Typography>
          <CategoryTag category={automation.category} />
          <Box sx={{ flex: 1 }} />
          {built ? (
            <FlowModeControl mode={mode} label={automation.title} onChange={onModeChange} />
          ) : (
            <Tooltip title={`This automation is built in Phase ${automation.phase}. Submit its templates now so they are approved by then.`}>
              <Box>
                <StatusPill tone="slate">Planned · Phase {automation.phase}</StatusPill>
              </Box>
            </Tooltip>
          )}
        </Stack>
        <Typography sx={{ fontSize: 13, color: UI.muted, mt: 0.5, lineHeight: 1.55 }}>{automation.summary}</Typography>
        {built && (
          <Typography sx={{ fontSize: 12, color: UI.muted, mt: 0.75 }}>
            {on && switchedOn.at ?
              `${mode === 'test' ? 'Test numbers only' : 'Live for eligible customers'}. Switched on${switchedOn.by ? ` by ${switchedOn.by}` : ''} on ${switchedOn.at.toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' })}. New matching events after switch-on are eligible.` :
              mode === 'off' ? 'Off. No automatic messages are sent.' : mode === 'test' ? 'Only saved test numbers receive this automation.' : 'Eligible customers receive this automation.'}
          </Typography>
        )}
        {built && automation.key === 'visitReminders' && testProjects.some((p) => p.latitude === null) && (
          <Alert severity="warning" sx={{ mt: 1, fontSize: 12.5, py: 0 }}>
            No site location in Website CMS for {testProjects.filter((p) => p.latitude === null).map((p) => p.name).join(', ')}:
            the 2-hour reminder (with the map pin) can’t be sent for visits to {testProjects.filter((p) => p.latitude === null).length === 1 ? 'it' : 'them'}.
            Add latitude/longitude to the project in Website CMS.
          </Alert>
        )}
        {built && offDependencies.length > 0 && (
          <Alert severity="info" sx={{ mt: 1, fontSize: 12.5, py: 0 }}>
            Button taps use these conversation flows, which are not available to every recipient in this journey’s mode: {offDependencies.join(', ')}. Set them to the matching mode so taps get a reply.
          </Alert>
        )}
        {built && oldItems.length > 0 && (
          <Box sx={{ mt: 1.25, border: `1px solid ${oldItems.some((i) => i.active) ? '#f0d9b5' : UI.line}`, bgcolor: oldItems.some((i) => i.active) ? '#fdf7ee' : 'transparent', borderRadius: 1.5, px: 1.5, py: 1 }}>
            <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: oldItems.some((i) => i.active) ? '#8a5a12' : UI.ink }}>
              Older automation that sends the same kind of message
            </Typography>
            <Typography sx={{ fontSize: 11.5, color: UI.muted, mb: 0.5 }}>
              {oldItems.some((item) => item.active) ?
                'Turn off the older customer automation before switching this journey Live to avoid duplicate messages.' :
                mode === 'live' ?
                  'The older customer automation is Off. This Live journey handles new matching visits.' :
                  'The older automation is Off.'}
            </Typography>
            {oldItems.map((item) => (
              <OldItemRow key={`${item.kind}-${item.id}`} item={item} api={oldApi} />
            ))}
          </Box>
        )}
        {automation.replacesExisting && (
          <Typography sx={{ fontSize: 12, color: '#8a5a12', mt: 0.75 }}>
            Replaces "{automation.replacesExisting}" once tested. The current one keeps running until then.
          </Typography>
        )}

        <Stack gap={1.5} sx={{ mt: 2 }}>
          {automation.steps.map((step, idx) => (
            <Stack key={step.title} direction="row" gap={1.25}>
              <Box sx={{ width: 22, height: 22, borderRadius: '50%', bgcolor: '#eef3f8', color: '#2f5f8a', fontSize: 11.5, fontWeight: 700, display: 'grid', placeItems: 'center', flexShrink: 0 }}>
                {idx + 1}
              </Box>
              <Box>
                <Typography sx={{ fontSize: 13.5, fontWeight: 600, color: UI.ink }}>{step.title}</Typography>
                <Typography sx={{ fontSize: 12, color: UI.muted }}>{step.detail}</Typography>
              </Box>
            </Stack>
          ))}
          {automation.buttonReplies && (
            <Stack direction="row" gap={1.25}>
              <Box sx={{ width: 22, height: 22, borderRadius: '50%', bgcolor: '#edf6ee', color: '#2f6b35', fontSize: 12, display: 'grid', placeItems: 'center', flexShrink: 0 }}>✓</Box>
              <Box>
                <Typography sx={{ fontSize: 13.5, fontWeight: 600, color: UI.ink }}>Button replies</Typography>
                <Typography sx={{ fontSize: 12, color: UI.muted }}>{automation.buttonReplies}</Typography>
              </Box>
            </Stack>
          )}
        </Stack>

        {built && <HubJourneyTools tool={automation.tool} />}

        <Box sx={{ mt: 2.5, border: `1px solid ${UI.line}`, borderRadius: 1.5 }}>
          <Stack direction="row" alignItems="center" sx={{ px: 1.5, py: 1, borderBottom: `1px solid ${UI.line}`, bgcolor: UI.canvas }}>
            <Typography sx={{ fontSize: 12, fontWeight: 600, color: UI.ink, flex: 1 }}>Meta templates</Typography>
            {missing && (
              <Button size="small" variant="outlined" disabled={!!templates.submitting} onClick={submitMissing} sx={{ ...textButtonSx, bgcolor: UI.surface }}>
                {templates.submitting ? 'Submitting…' : 'Submit to Meta'}
              </Button>
            )}
          </Stack>
          {automation.templates.map((name) => {
            const d = drafts.find((draft) => draft.name === name);
            return <Stack key={name} direction="row" alignItems="center" gap={1} flexWrap="wrap" sx={{ px: 1.5, py: 1, borderTop: `1px solid ${UI.line}`, '&:first-of-type': { borderTop: 'none' } }}>
              <Box
                component="button"
                type="button"
                onClick={() => {
                  setPreviewName(name);
                  setTestResult(null);
                }}
                sx={{ fontFamily: UI.mono, fontSize: 12, color: name === previewName ? UI.gold : UI.ink, bgcolor: 'transparent', border: 'none', p: 0, cursor: 'pointer', textAlign: 'left', flex: 1, minWidth: 180 }}
              >
                {name}
              </Box>
              {(d ? AUTO_REPLY_LANGS : AUTO_REPLY_LANGS.filter(({ key }) => key === 'en')).map(({ key }) => (
                <TemplateStatusChip key={key} lang={key} status={templates.statusFor(name, key)} />
              ))}
              {!d && <Typography sx={{ fontSize: 11.5, color: UI.muted }}>Existing Meta template · managed in older templates</Typography>}
            </Stack>;
          })}
          {!!automation.manualTemplates?.length && (
            <Box sx={{ borderTop: `1px solid ${UI.line}`, px: 1.5, py: 1.25, bgcolor: '#fffdf8' }}>
              <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
                <Box sx={{ flex: 1, minWidth: 200 }}>
                  <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: UI.ink }}>Available for manual sending</Typography>
                  <Typography sx={{ fontSize: 11.5, color: UI.muted, mt: 0.25 }}>
                    Sent by staff from Office → Unit detail → Payment transactions. This template does not send automatically.
                  </Typography>
                </Box>
                <Button size="small" variant="outlined" onClick={onOpenTemplates} sx={{ ...textButtonSx, bgcolor: UI.surface }}>
                  View in Templates
                </Button>
              </Stack>
              {automation.manualTemplates.map((name) => (
                <Stack key={name} direction="row" alignItems="center" gap={1} flexWrap="wrap" sx={{ mt: 1, pt: 1, borderTop: `1px solid ${UI.line}` }}>
                  <Typography sx={{ fontFamily: UI.mono, fontSize: 12, color: UI.ink, flex: 1, minWidth: 180 }}>{name}</Typography>
                  {AUTO_REPLY_LANGS.map(({ key }) => (
                    <TemplateStatusChip key={key} lang={key} status={templates.statusFor(name, key)} />
                  ))}
                </Stack>
              ))}
            </Box>
          )}
        </Box>
        {submitError && <Alert severity="error" sx={{ mt: 1, fontSize: 12.5, py: 0 }}>{submitError}</Alert>}
        <Typography sx={{ fontSize: 11.5, color: UI.muted, mt: 0.75 }}>
          {automation.key === 'visitBrochure' ?
            'This existing Meta template is approved in English and uses the project brochure PDF and details button.' :
            'Meta usually reviews templates within minutes, sometimes up to 24 hours. Meta may move a Utility template to Marketing.'}
        </Typography>

        {built && (
          <Typography sx={{ fontSize: 12.5, color: UI.muted, mt: 2 }}>
            Last 30 days: sent <b>{stats.sent}</b> · skipped <b>{skippedTotal}</b>
          </Typography>
        )}
        <Box component="details" sx={{ mt: 1.25, border: `1px solid ${UI.line}`, borderRadius: 1.5, px: 1.5, py: 1, '& summary': { cursor: 'pointer', fontSize: 12.5, fontWeight: 600, color: UI.ink } }}>
          <summary>Won't send when…{built ? ` (${skippedTotal} skipped in 30 days)` : ''}</summary>
          {skippedTotal > 0 && (
            <Box sx={{ mt: 0.75, fontSize: 12.5, color: UI.ink }}>
              {Object.entries(stats.skipped).map(([reason, count]) => (
                <div key={reason}>
                  {SKIP_REASON_LABEL[reason] || reason}: {count}
                </div>
              ))}
            </Box>
          )}
          <Box component="ul" sx={{ m: 0, mt: 0.75, pl: 2.5, fontSize: 12.5, color: UI.muted, lineHeight: 1.7 }}>
            {automation.skipWhen.map((reason) => (
              <li key={reason}>{reason}</li>
            ))}
          </Box>
        </Box>
      </Box>

      {preview && (
        <Box>
          <ToggleButtonGroup
            exclusive
            fullWidth
            size="small"
            value={lang}
            onChange={(_, value) => value && setLang(value)}
            sx={{
              mb: 1.25,
              p: 0.25,
              bgcolor: UI.canvas,
              borderRadius: 1.5,
              '& .MuiToggleButton-root': { textTransform: 'none', fontSize: 12.5, border: 'none', borderRadius: '6px !important', py: 0.4, color: UI.muted },
              '& .Mui-selected': { bgcolor: `${UI.surface} !important`, boxShadow: `0 1px 2px ${UI.line}`, fontWeight: 600, color: `${UI.ink} !important` },
            }}
          >
            {AUTO_REPLY_LANGS.map((l) => (
              <ToggleButton key={l.key} value={l.key}>
                {l.label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
          <WhatsappPhonePreview
            header={
              preview.header?.type === 'LOCATION' ? (
                <Stack alignItems="center" justifyContent="center" sx={{ height: 90, bgcolor: '#e4ecdf', borderRadius: 1.25, mb: 0.75, color: '#c62828' }}>
                  <PlaceOutlinedIcon />
                  <Typography sx={{ fontSize: 11, color: '#667781' }}>Site location</Typography>
                </Stack>
              ) : undefined
            }
            body={previewBody || templatePreviewText(preview, lang)}
            footer={preview.footer?.[lang]}
            buttons={(preview.buttons || []).map((b) => b[lang])}
          />
          <Typography sx={{ fontSize: 11.5, color: UI.faint, mt: 0.75 }}>
            {preview.name} · sample values · {preview.purpose}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

type Selection = { kind: 'flow' | 'journey'; key: string };

export type AutomationsMode = 'conversations' | 'journeys' | 'customerFlow';

const CustomerActivity = () => {
  const [phone, setPhone] = useState('');
  const [events, setEvents] = useState<Array<Record<string, any>>>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [searched, setSearched] = useState(false);
  const search = async () => {
    const phone10 = toPhone10(phone);
    if (!phone10) {
      setError('Enter a 10-digit customer mobile number.');
      return;
    }
    setBusy(true);
    setError('');
    setSearched(true);
    try {
      const snap = await getDocs(query(collection(db, 'whatsappAutoReplyLogs'), where('phone10', '==', phone10)));
      setEvents(snap.docs.map((d) => d.data() as Record<string, any>).sort((a, b) =>
        (b.at?.toMillis?.() || 0) - (a.at?.toMillis?.() || 0)).slice(0, 100));
    } catch (err: any) {
      setEvents([]);
      setError(`Could not load activity: ${err?.message || err}`);
    } finally {
      setBusy(false);
    }
  };
  return (
    <Box component="details" sx={{ bgcolor: UI.surface, border: `1px solid ${UI.line}`, borderRadius: 2.5, p: 2, '& summary': { cursor: 'pointer', fontWeight: 600, fontSize: 14, color: UI.ink } }}>
      <summary>Check one customer's automation activity</summary>
      <Typography sx={{ fontSize: 12, color: UI.muted, mt: 0.75, mb: 1.5 }}>
        Recent sent, skipped and button-tap events recorded for this number. This is history, not a promise that future messages will send.
      </Typography>
      <Stack direction="row" gap={1} flexWrap="wrap">
        <TextField size="small" label="Customer mobile" value={phone} onChange={(e) => setPhone(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') void search(); }} sx={{ width: 230 }} />
        <Button variant="outlined" disabled={busy} onClick={search} sx={textButtonSx}>{busy ? 'Checking…' : 'Check activity'}</Button>
      </Stack>
      {error && <Alert severity="error" sx={{ mt: 1.5 }}>{error}</Alert>}
      {searched && !busy && !error && (
        <Stack gap={0.75} sx={{ mt: 1.5, maxHeight: 300, overflowY: 'auto' }}>
          {events.length === 0 ? <Typography sx={{ fontSize: 12.5, color: UI.muted }}>No recorded automation activity for this number.</Typography> : events.map((event, index) => (
            <Stack key={`${event.flow || 'event'}-${index}`} direction="row" alignItems="baseline" gap={1} flexWrap="wrap" sx={{ borderBottom: `1px solid ${UI.line}`, pb: 0.5 }}>
              <Typography sx={{ fontSize: 12, fontWeight: 700, color: event.kind === 'sent' ? '#2f6b35' : UI.muted, minWidth: 55 }}>{event.kind || 'event'}</Typography>
              <Typography sx={{ fontSize: 12.5, color: UI.ink }}>{event.flow || 'WhatsApp'}{event.message && event.message !== event.flow ? ` · ${event.message}` : ''}</Typography>
              {event.reason && <Typography sx={{ fontSize: 12, color: UI.muted }}>· {SKIP_REASON_LABEL[event.reason] || event.reason}</Typography>}
              <Typography sx={{ fontSize: 11.5, color: UI.faint, ml: 'auto' }}>{event.at?.toDate?.()?.toLocaleString('en-IN') || ''}</Typography>
            </Stack>
          ))}
        </Stack>
      )}
    </Box>
  );
};

const HubAutomationsTab = ({ templates, mode }: { templates: TemplateStatusesApi; mode: AutomationsMode }) => {
  const { currentUser } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [settings, setSettings] = useState<WhatsappAutoReplySettings>(DEFAULT_AUTO_REPLY_SETTINGS);
  const [sentCounts, setSentCounts] = useState<Record<string, number>>({});
  const [skipped, setSkipped] = useState<Record<string, Record<string, number>>>({});
  const [rawSettings, setRawSettings] = useState<Record<string, any>>({});
  const old = useOldAutomation();
  const [undoOffer, setUndoOffer] = useState<{ title: string; items: OldItem[] } | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [confirm, confirmDialog] = useConfirmAction();

  // Journeys switched to Test / Live whose templates cannot send yet.
  const issuesFor = (a: JourneyAutomation) => templateIssues(a.templates, templates);
  const stuckJourneys = JOURNEY_AUTOMATIONS
    .filter((a) => isJourneyBuilt(a) && getFlowMode(settings, a.key) !== 'off')
    .map((a) => ({ automation: a, issues: issuesFor(a) }))
    .filter((row) => row.issues.length > 0);
  const blockedCount = stuckJourneys.filter((row) => row.issues.some((i) => i.blocking)).length;
  const openTab = (tab: string, a?: string) => {
    const params = new URLSearchParams(searchParams);
    params.set('tab', tab);
    if (a) params.set('a', a); else params.delete('a');
    setSearchParams(params);
  };

  const selection: Selection = useMemo(() => {
    const raw = String(searchParams.get('a') || '');
    const [kind, key] = raw.split(':');
    if (mode === 'journeys') {
      if (kind === 'journey' && JOURNEY_AUTOMATIONS.some((a) => a.key === key)) return { kind, key };
      return { kind: 'journey', key: JOURNEY_AUTOMATIONS[0].key };
    }
    if (mode === 'customerFlow') {
      if (kind === 'journey' && JOURNEY_AUTOMATIONS.some((a) => a.key === key)) return { kind, key };
      if (kind === 'flow' && AUTO_REPLY_FLOWS.some((f) => f.key === key)) return { kind, key };
      return { kind: 'journey', key: 'enquiryWelcome' };
    }
    if (kind === 'flow' && AUTO_REPLY_FLOWS.some((f) => f.key === key)) return { kind, key };
    return { kind: 'flow', key: CONVERSATION_FLOWS[0].key };
  }, [searchParams, mode]);

  const select = (next: Selection) => {
    const params = new URLSearchParams(searchParams);
    params.set('a', `${next.kind}:${next.key}`);
    setSearchParams(params, { replace: true });
    if (mode === 'customerFlow') {
      window.requestAnimationFrame(() => document.getElementById('customer-flow-editor')?.scrollIntoView({ behavior: 'smooth', block: 'start' }));
    }
  };

  useEffect(
    () =>
      onSnapshot(
        doc(db, WHATSAPP_SETTINGS_COLLECTION, WHATSAPP_AUTO_REPLY_DOC),
        (snap) => {
          setSettings(snap.exists() ? normalizeAutoReplySettings(snap.data()) : DEFAULT_AUTO_REPLY_SETTINGS);
          setRawSettings(snap.exists() ? (snap.data() as Record<string, any>) : {});
        },
        () => undefined
      ),
    []
  );

  useEffect(() => {
    const since = Timestamp.fromMillis(Date.now() - 30 * 86400000);
    getDocs(query(collection(db, 'whatsappAutoReplyLogs'), where('at', '>=', since), limit(3000)))
      .then((snap) => {
        const counts: Record<string, number> = {};
        const skips: Record<string, Record<string, number>> = {};
        snap.docs.forEach((d) => {
          const row = d.data() as any;
          if (!row.flow) return;
          if (row.kind === 'sent') counts[row.flow] = (counts[row.flow] || 0) + 1;
          if (row.kind === 'skipped') {
            const reason = String(row.reason || 'other');
            skips[row.flow] = { ...(skips[row.flow] || {}), [reason]: (skips[row.flow]?.[reason] || 0) + 1 };
          }
        });
        setSentCounts(counts);
        setSkipped(skips);
      })
      .catch(() => setSentCounts({}));
  }, []);

  const toggleFlow = async (key: string, title: string) => {
    const next = !isFlowEnabled(settings, key);
    try {
      await setDoc(
        doc(db, WHATSAPP_SETTINGS_COLLECTION, WHATSAPP_AUTO_REPLY_DOC),
        {
          flowEnabled: { [key]: next },
          ...(next ? { flowEnabledAt: { [key]: serverTimestamp() }, flowEnabledBy: { [key]: currentUser?.email || null } } : {}),
          updatedAt: serverTimestamp(),
          updatedByEmail: currentUser?.email || null,
        },
        { merge: true }
      );
      setNotice(settings.automationPaused && next ? `${title} is on, but all automation is paused in Settings.` : null);
      // Turning a new flow off: offer to turn its older replacement back on.
      const automation = JOURNEY_AUTOMATIONS.find((a) => a.key === key);
      const restorable = !next && automation ? old.overlapsFor(automation).filter((i) => !i.active && i.disabledByHub) : [];
      setUndoOffer(restorable.length ? { title, items: restorable } : null);
    } catch (err: any) {
      setNotice(`Could not switch ${title}: ${err?.message || err}`);
    }
  };

  const setFlowMode = async (key: string, title: string, next: FlowMode) => {
    const previous = getFlowMode(settings, key);
    if (next === previous) return;
    if (next === 'test' && settings.testNumbers.length === 0) {
      setNotice('Add a test number in WhatsApp Settings before selecting Test.');
      return;
    }
    const overlapping = JOURNEY_AUTOMATIONS.find((a) => a.key === key);
    const blocking = next !== 'off' && overlapping ? issuesFor(overlapping).filter((i) => i.blocking) : [];
    if (blocking.length && !(await confirm({
      title: `${title} won't send yet`,
      subject: blocking.map((i) => i.text).join(' · '),
      body: `Nothing goes out until Meta approves the English template. Switch to ${next === 'live' ? 'Live' : 'Test'} anyway?`,
      confirmLabel: `Yes, switch to ${next === 'live' ? 'Live' : 'Test'}`,
      tone: 'danger',
    }))) return;
    const activeOld = next === 'live' && overlapping ? old.overlapsFor(overlapping).filter((item) => item.active) : [];
    if (activeOld.length > 0) {
      setNotice(`Turn off the overlapping older automation before setting ${title} Live: ${activeOld.map((item) => item.name).join(', ')}.`);
      return;
    }
    try {
      await setDoc(doc(db, WHATSAPP_SETTINGS_COLLECTION, WHATSAPP_AUTO_REPLY_DOC), {
        flowModes: { [key]: next },
        // The deployed legacy sender only understands this boolean. Keep Test
        // false there so it cannot accidentally send live before deployment.
        flowEnabled: { [key]: next === 'live' },
        ...(previous === 'off' && next !== 'off' ? {
          flowEnabledAt: { [key]: serverTimestamp() },
          flowEnabledBy: { [key]: currentUser?.email || null },
        } : {}),
        updatedAt: serverTimestamp(),
        updatedByEmail: currentUser?.email || null,
      }, { merge: true });
      setNotice(settings.automationPaused && next !== 'off' ? `${title} is ${next}, but all automation is paused in Settings.` : null);
      const automation = JOURNEY_AUTOMATIONS.find((a) => a.key === key);
      const restorable = next === 'off' && automation ? old.overlapsFor(automation).filter((i) => !i.active && i.disabledByHub) : [];
      setUndoOffer(restorable.length ? { title, items: restorable } : null);
    } catch (err: any) {
      setNotice(`Could not change ${title}: ${err?.message || err}`);
    }
  };

  const journeyGroups = [
    { label: 'UTILITY · CHEAPER', items: JOURNEY_AUTOMATIONS.filter((a) => a.category === 'UTILITY') },
    { label: 'MARKETING · ALWAYS CHARGED', items: JOURNEY_AUTOMATIONS.filter((a) => a.category === 'MARKETING') },
  ];
  const selectedJourney = selection.kind === 'journey' ? JOURNEY_AUTOMATIONS.find((a) => a.key === selection.key) : null;

  return (
    <Stack gap={2}>
      {confirmDialog}
      {stuckJourneys.length > 0 && (
        <Alert severity={blockedCount ? 'error' : 'warning'} sx={{ fontSize: 12.5, py: 0.25 }}
          action={<Button size="small" onClick={() => openTab('templates')} sx={{ textTransform: 'none', fontWeight: 600, whiteSpace: 'nowrap' }}>Open Templates</Button>}>
          <Typography sx={{ fontSize: 12.5, fontWeight: 700 }}>
            {blockedCount ?
              `${blockedCount} automation${blockedCount > 1 ? 's are' : ' is'} switched on but can't send: template missing or not approved.` :
              'Some switched-on automations are missing a language and send in English instead.'}
          </Typography>
          {stuckJourneys.map(({ automation, issues }) => (
            <Box key={automation.key} sx={{ mt: 0.25 }}>
              <Box component="button" type="button" onClick={() => openTab('journeys', `journey:${automation.key}`)}
                sx={{ border: 'none', bgcolor: 'transparent', p: 0, cursor: 'pointer', font: 'inherit', fontWeight: 600, color: 'inherit', textDecoration: 'underline' }}>
                {automation.title} ({getFlowMode(settings, automation.key) === 'live' ? 'Live' : 'Test'})
              </Box>
              {`: ${issues.map((i) => i.text).join('; ')}`}
            </Box>
          ))}
        </Alert>
      )}
      {settings.automationPaused && <Alert severity="warning" sx={{ fontSize: 12.5, py: 0 }}>All WhatsApp automation is paused in Settings. Off, Test and Live selections are saved, but nothing sends automatically.</Alert>}
      {mode === 'customerFlow' ? (
        <>
          <Box sx={{ bgcolor: UI.surface, border: `1px solid ${UI.line}`, borderRadius: 2.5, px: 2, py: 1.5 }}>
            <Stack direction="row" alignItems="center" gap={1.5} flexWrap="wrap">
              <Box sx={{ flex: 1, minWidth: 250 }}>
                <Typography sx={{ fontSize: 15, fontWeight: 700, color: UI.ink }}>Customer journey, from enquiry to possession</Typography>
                <Typography sx={{ fontSize: 12.5, color: UI.muted }}>Select a step to see its connected workflow and edit its messages and buttons directly on the diagram.</Typography>
              </Box>
              {settings.automationPaused && <StatusPill tone="amber">All automation paused</StatusPill>}
              <Typography sx={{ fontSize: 11.5, color: UI.muted }}>{settings.testNumbers.length} test numbers</Typography>
            </Stack>
          </Box>
          <Box sx={{ bgcolor: UI.surface, border: `1px solid ${UI.line}`, borderRadius: 2.5, p: 1.5, overflowX: 'auto' }}>
            <Stack direction="row" justifyContent="space-between" gap={1} flexWrap="wrap" sx={{ mb: 1.25 }}>
              <Typography sx={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.7, color: UI.muted }}>CUSTOMER FLOW · FOLLOW THE ARROWS FOR POSSIBLE NEXT STAGES</Typography>
              <Typography sx={{ fontSize: 11, color: UI.gold }}>Scroll right for later stages →</Typography>
            </Stack>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(6, minmax(196px, 1fr))', gap: '24px', minWidth: 1296, pb: 0.5 }}>
              {JOURNEY_STAGES.map((stage, index) => {
                const stageAutomations = JOURNEY_AUTOMATIONS.filter((a) => a.stage === stage.key);
                const branchKeys = FLOW_STAGE_KEYS[stage.key] || [];
                return (
                  <Box key={stage.key} sx={{ minWidth: 0, position: 'relative' }}>
                    <Stack direction="row" alignItems="center" gap={1} sx={{ height: 62, px: 1.25, borderRadius: 1.75, bgcolor: UI.ink, color: UI.surface, position: 'relative', zIndex: 1,
                      ...(index < JOURNEY_STAGES.length - 1 ? { '&::after': { content: '""', position: 'absolute', width: 24, right: -24, top: '50%', borderTop: `2px solid ${UI.gold}` } } : {}) }}>
                      <Box sx={{ width: 25, height: 25, borderRadius: '50%', bgcolor: UI.gold, color: UI.surface, fontSize: 12, fontWeight: 700, display: 'grid', placeItems: 'center', flexShrink: 0 }}>{index + 1}</Box>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{ fontSize: 10, letterSpacing: 0.6, opacity: 0.7 }}>STAGE {index + 1}</Typography>
                        <Typography sx={{ fontSize: 13, fontWeight: 700, lineHeight: 1.2 }}>{stage.title}</Typography>
                      </Box>
                    </Stack>
                    {index < JOURNEY_STAGES.length - 1 && <Box aria-hidden="true" sx={{ position: 'absolute', top: 25, right: -24, color: UI.gold, fontSize: 18, lineHeight: 1, zIndex: 2 }}>›</Box>}
                    <Box sx={{ ml: 1.5, pl: 1.5, pt: 1.25, borderLeft: `2px solid ${UI.gold}`, minHeight: 60 }}>
                      <Typography sx={{ fontSize: 10, fontWeight: 700, letterSpacing: 0.5, color: UI.gold, mb: 0.75 }}>WHEN CRM EVENT OCCURS</Typography>
                      <Stack gap={0.75}>
                        {stageAutomations.map((a) => {
                          const flowMode = getFlowMode(settings, a.key);
                          const on = flowMode !== 'off';
                          const { approved, total } = approvalSummary(a.templates, templates);
                          const selected = selection.kind === 'journey' && selection.key === a.key;
                          return (
                            <Box key={a.key} component="button" type="button" onClick={() => select({ kind: 'journey', key: a.key })}
                              sx={{ fontFamily: UI.body, textAlign: 'left', width: '100%', p: 1, position: 'relative', border: `1px solid ${selected ? UI.gold : UI.line}`, borderRadius: 1.25, bgcolor: selected ? UI.goldTint : UI.surface, cursor: 'pointer', '&:hover': { borderColor: UI.gold }, '&:focus-visible': { outline: `2px solid ${UI.gold}` }, '&::before': { content: '""', position: 'absolute', width: 14, left: -16, top: 20, borderTop: `2px solid ${UI.gold}` } }}>
                              <Stack direction="row" gap={0.5} alignItems="center">
                                <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: on ? '#3f9a48' : UI.faint, flexShrink: 0 }} />
                                <Typography sx={{ fontSize: 12, fontWeight: 600, color: UI.ink, flex: 1 }}>{a.title}</Typography>
                                <Typography sx={{ fontSize: 10.5, color: on ? '#2f6b35' : UI.faint }}>{flowMode === 'test' ? 'Test' : flowMode === 'live' ? 'Live' : 'Off'}</Typography>
                              </Stack>
                              <Typography sx={{ fontSize: 10.5, color: UI.muted, mt: 0.35 }}>{approved}/{total} languages approved</Typography>
                              <Typography sx={{ fontSize: 10.5, color: UI.muted, mt: 0.35, lineHeight: 1.3 }}>{a.steps[0]?.title}</Typography>
                            </Box>
                          );
                        })}
                      </Stack>
                      {branchKeys.length > 0 && (
                        <Box sx={{ mt: 1.25, pt: 1, borderTop: `1px dashed ${UI.line}` }}>
                          <Typography sx={{ fontSize: 10, fontWeight: 700, letterSpacing: 0.5, color: UI.muted, mb: 0.75 }}>IF CUSTOMER WRITES OR TAPS</Typography>
                          <Stack gap={0.75}>
                            {branchKeys.map((key) => {
                              const flow = AUTO_REPLY_FLOWS.find((f) => f.key === key);
                              if (!flow) return null;
                              const on = getFlowMode(settings, key) !== 'off';
                              const selected = selection.kind === 'flow' && selection.key === key;
                              return (
                                <Box key={key} component="button" type="button" onClick={() => select({ kind: 'flow', key })}
                                  sx={{ fontFamily: UI.body, textAlign: 'left', p: 0.8, position: 'relative', border: `1px dashed ${selected ? UI.gold : UI.line}`, borderRadius: 1.25, bgcolor: selected ? UI.goldTint : UI.canvas, cursor: 'pointer', '&:hover': { borderColor: UI.gold }, '&:focus-visible': { outline: `2px solid ${UI.gold}` }, '&::before': { content: '""', position: 'absolute', width: 14, left: -16, top: 17, borderTop: `2px dashed ${UI.gold}` } }}>
                                  <Typography sx={{ fontSize: 11.5, fontWeight: 600, color: UI.ink }}>{flow.title} · {on ? 'On' : 'Off'}</Typography>
                                  <Typography sx={{ fontSize: 10.5, color: UI.muted }}>Customer-triggered reply</Typography>
                                </Box>
                              );
                            })}
                          </Stack>
                        </Box>
                      )}
                    </Box>
                  </Box>
                );
              })}
            </Box>
            <Typography sx={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.7, color: UI.muted, mt: 2, mb: 0.75 }}>OPTIONAL MARKETING AND SHARED RESPONSES</Typography>
            <Stack direction="row" gap={0.75} flexWrap="wrap">
              {JOURNEY_AUTOMATIONS.filter((a) => a.stage === 'marketing').map((a) => (
                <Box key={a.key} component="button" type="button" onClick={() => select({ kind: 'journey', key: a.key })}
                  sx={{ fontFamily: UI.body, fontSize: 11.5, fontWeight: 600, color: UI.ink, bgcolor: selection.kind === 'journey' && selection.key === a.key ? UI.goldTint : UI.canvas, border: `1px solid ${selection.kind === 'journey' && selection.key === a.key ? UI.gold : UI.line}`, borderRadius: 1.25, px: 1.2, py: 0.8, cursor: 'pointer' }}>
                  {a.title} · {isFlowEnabled(settings, a.key) ? 'On' : 'Off'}
                </Box>
              ))}
              {['optOut', 'shared'].map((key) => {
                const flow = AUTO_REPLY_FLOWS.find((f) => f.key === key);
                if (!flow) return null;
                return (
                  <Box key={key} component="button" type="button" onClick={() => select({ kind: 'flow', key })}
                    sx={{ fontFamily: UI.body, fontSize: 11.5, fontWeight: 600, color: UI.ink, bgcolor: selection.kind === 'flow' && selection.key === key ? UI.goldTint : UI.canvas, border: `1px solid ${selection.kind === 'flow' && selection.key === key ? UI.gold : UI.line}`, borderRadius: 1.25, px: 1.2, py: 0.8, cursor: 'pointer' }}>
                  {flow.title} · {getFlowMode(settings, key) === 'off' ? 'Off' : getFlowMode(settings, key) === 'test' ? 'Test' : 'Live'}
                  </Box>
                );
              })}
            </Stack>
          </Box>
          <Typography sx={{ fontSize: 11.5, color: UI.muted }}>Arrows show possible stage progression. Solid branches are CRM-triggered messages; dashed branches respond when a customer writes or taps. Marketing messages are optional. A customer receives only messages whose trigger, switch and sending rules match.</Typography>
          <CustomerFlowCanvas selection={selection} settings={settings} templates={templates} onModeChange={setFlowMode} onNavigate={select} />
          <CustomerActivity />
        </>
      ) : mode === 'conversations' ? (
        <Typography sx={{ fontSize: 12.5, color: UI.muted }}>
          Replies to customers who message you first. They go inside WhatsApp's 24-hour reply window, so they are <b>free</b> and need no Meta template.
        </Typography>
      ) : (
        <Typography sx={{ fontSize: 12.5, color: UI.muted }}>
          Messages Swarna starts, sent as Meta-approved templates. <b>Charged per message</b>: utility templates are free only inside a 24-hour reply window, marketing templates are always charged.
        </Typography>
      )}

      {mode === 'journeys' && old.items.some((i) => i.active || i.disabledByHub) && (
        <Box component="details" sx={{ border: `1px solid #f0d9b5`, bgcolor: '#fdf7ee', borderRadius: 2, px: 1.5, py: 1, '& summary': { cursor: 'pointer', fontSize: 12.5, fontWeight: 600, color: '#8a5a12' } }}>
          <summary>
            Older automation: {old.items.filter((i) => i.active).length} still on
            {old.items.some((i) => !i.active && i.disabledByHub) ? `, ${old.items.filter((i) => !i.active && i.disabledByHub).length} turned off from here` : ''}
          </summary>
          <Typography sx={{ fontSize: 11.5, color: UI.muted, mt: 0.5 }}>
            Keep them on while testing. Before setting an overlapping journey to Live, turn off its older rule to avoid duplicate messages.
          </Typography>
          <Box sx={{ mt: 0.5 }}>
            {old.items
              .filter((i) => i.active || i.disabledByHub)
              .map((item) => {
                const replacedBy = JOURNEY_AUTOMATIONS.filter((a) => a.oldRuleTriggers?.includes(item.trigger) &&
                  (a.key !== 'visitBrochure' || item.recipient === 'customer')).map((a) => a.title);
                return (
                  <OldItemRow
                    key={`${item.kind}-${item.id}`}
                    item={item}
                    api={old}
                    note={replacedBy.length ? <Box component="span" sx={{ color: '#8a5a12' }}> · overlaps {replacedBy.join(', ')}</Box> : <Box component="span"> · no new flow replaces it yet</Box>}
                  />
                );
              })}
          </Box>
        </Box>
      )}

      {/* Journey strip */}
      {mode === 'journeys' && (
      <Box sx={{ bgcolor: UI.surface, border: `1px solid ${UI.line}`, borderRadius: 2.5, p: 1.5, overflowX: 'auto' }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(6, minmax(150px, 1fr))', gap: 1.25, minWidth: 920 }}>
          {JOURNEY_STAGES.map((stage, idx) => (
            <Box key={stage.key}>
              <Stack direction="row" alignItems="center" gap={0.75} sx={{ pb: 0.75, mb: 0.75, borderBottom: `2px solid ${UI.line}` }}>
                <Box sx={{ width: 18, height: 18, borderRadius: '50%', bgcolor: UI.ink, color: UI.surface, fontSize: 10.5, fontWeight: 700, display: 'grid', placeItems: 'center' }}>{idx + 1}</Box>
                <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: UI.ink }}>{stage.title}</Typography>
              </Stack>
              <Stack gap={0.5}>
                {JOURNEY_AUTOMATIONS.filter((a) => a.stage === stage.key).map((a) => {
                  const selected = selection.kind === 'journey' && selection.key === a.key;
                  return (
                    <Stack
                      key={a.key}
                      direction="row"
                      alignItems="center"
                      gap={0.75}
                      component="button"
                      type="button"
                      onClick={() => select({ kind: 'journey', key: a.key })}
                      sx={{
                        fontFamily: UI.body,
                        textAlign: 'left',
                        border: `1px solid ${selected ? UI.gold : UI.line}`,
                        bgcolor: selected ? UI.goldTint : UI.surface,
                        borderRadius: 1.25,
                        px: 1,
                        py: 0.6,
                        cursor: 'pointer',
                        '&:hover': { borderColor: selected ? UI.gold : UI.faint },
                      }}
                    >
                      <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: isJourneyBuilt(a) && getFlowMode(settings, a.key) !== 'off' ? (getFlowMode(settings, a.key) === 'live' ? '#3f9a48' : UI.gold) : UI.faint, flexShrink: 0 }} />
                      <Typography sx={{ fontSize: 12, color: UI.ink, flex: 1, minWidth: 0 }}>{a.title}</Typography>
                      <Typography sx={{ fontSize: 10.5, color: isJourneyBuilt(a) && isFlowEnabled(settings, a.key) ? '#2f6b35' : UI.faint }}>
                        {isJourneyBuilt(a) ? (getFlowMode(settings, a.key) === 'off' ? 'Off' : getFlowMode(settings, a.key) === 'test' ? 'Test' : 'Live') : `P${a.phase}`}
                      </Typography>
                    </Stack>
                  );
                })}
              </Stack>
            </Box>
          ))}
        </Box>
      </Box>
      )}

      {notice && (
        <Alert severity="info" onClose={() => setNotice(null)} sx={{ fontSize: 12.5, py: 0 }}>
          {notice}
        </Alert>
      )}
      {undoOffer && (
        <Alert
          severity="warning"
          onClose={() => setUndoOffer(null)}
          sx={{ fontSize: 12.5, py: 0, alignItems: 'center' }}
          action={
            <Button
              size="small"
              onClick={async () => {
                await Promise.all(undoOffer.items.map((i) => old.setActive(i, true)));
                setUndoOffer(null);
                setNotice(`Older automation turned back on: ${undoOffer.items.map((i) => i.name).join(', ')}.`);
              }}
              sx={{ textTransform: 'none', fontWeight: 600 }}
            >
              Turn back on
            </Button>
          }
        >
          {undoOffer.title} is off. The older automation it replaced is also off ({undoOffer.items.map((i) => i.name).join(', ')}), so nothing sends this message now.
        </Alert>
      )}

      {mode !== 'customerFlow' && <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '320px minmax(0, 1fr)' }, gap: 2, alignItems: 'start', scrollMarginTop: 12 }}>
        <Box sx={{ bgcolor: UI.surface, border: `1px solid ${UI.line}`, borderRadius: 2.5, pb: 1, position: { md: 'sticky' }, top: 0 }}>
          {mode === 'conversations' && (
          <>
          <GroupLabel>CONVERSATIONS · FREE</GroupLabel>
          {CONVERSATION_FLOWS.map((f) => {
            const flowMode = getFlowMode(settings, f.key);
            return (
              <ListRow
                key={f.key}
                title={f.title}
                sub={`${sentCounts[f.key] || 0} sent in 30 days`}
                selected={selection.kind === 'flow' && selection.key === f.key}
                onClick={() => select({ kind: 'flow', key: f.key })}
                right={
                  <FlowModeControl compact mode={flowMode} label={f.title}
                    onChange={(next) => setFlowMode(f.key, f.title, next)} />
                }
              />
            );
          })}
          <ListRow
            title="Shared footer"
            sub="Under messages with buttons"
            selected={selection.kind === 'flow' && selection.key === 'shared'}
            onClick={() => select({ kind: 'flow', key: 'shared' })}
            right={
              <Switch
                size="small"
                checked={isFlowEnabled(settings, 'shared')}
                onClick={(e) => e.stopPropagation()}
                onChange={() => toggleFlow('shared', 'Shared footer')}
                inputProps={{ 'aria-label': 'Shared footer on or off' }}
                sx={switchSx}
              />
            }
          />
          </>
          )}
          {mode === 'journeys' && journeyGroups.map((group) => (
            <React.Fragment key={group.label}>
              <GroupLabel>{group.label}</GroupLabel>
              {group.items.map((a) => {
                const { approved, total } = approvalSummary(a.templates, templates);
                const rowIssues = isJourneyBuilt(a) && getFlowMode(settings, a.key) !== 'off' ? issuesFor(a) : [];
                return (
                  <ListRow
                    key={a.key}
                    title={a.title}
                    warning={rowIssues.length ? (rowIssues.some((i) => i.blocking) ? "Can't send: template not approved" : 'Some languages send in English') : undefined}
                    sub={
                      isJourneyBuilt(a) ?
                        `${sentCounts[a.key] || 0} sent in 30 days · ${approved}/${total} approved` :
                        `Phase ${a.phase} · ${approved}/${total} templates approved`
                    }
                    selected={selection.kind === 'journey' && selection.key === a.key}
                    onClick={() => select({ kind: 'journey', key: a.key })}
                    right={
                      isJourneyBuilt(a) ? (
                        <FlowModeControl compact mode={getFlowMode(settings, a.key)} label={a.title}
                          onChange={(next) => setFlowMode(a.key, a.title, next)} />
                      ) : (
                        <Typography sx={{ fontSize: 11, color: UI.faint }}>Planned</Typography>
                      )
                    }
                  />
                );
              })}
            </React.Fragment>
          ))}
        </Box>

        <Box sx={{ minWidth: 0 }}>
          {selection.kind === 'flow' ? (
            <AutoReplyMessagesSection flowKey={selection.key} hideFlowNav />
          ) : selectedJourney ? (
            <Box sx={{ bgcolor: UI.surface, border: `1px solid ${UI.line}`, borderRadius: 2.5 }}>
              <JourneyDetail
                automation={selectedJourney}
                templates={templates}
                mode={getFlowMode(settings, selectedJourney.key)}
                onModeChange={(next) => setFlowMode(selectedJourney.key, selectedJourney.title, next)}
                switchedOn={{
                  at: rawSettings.flowEnabledAt?.[selectedJourney.key]?.toDate?.() || null,
                  by: rawSettings.flowEnabledBy?.[selectedJourney.key] || null,
                }}
                stats={{ sent: sentCounts[selectedJourney.key] || 0, skipped: skipped[selectedJourney.key] || {} }}
                oldItems={old.overlapsFor(selectedJourney)}
                oldApi={old}
                onOpenTemplates={() => openTab('templates')}
                offDependencies={(selectedJourney.dependsOn || [])
                  .filter((k) => getFlowMode(settings, k) === 'off' || (getFlowMode(settings, selectedJourney.key) === 'live' && getFlowMode(settings, k) !== 'live'))
                  .map((k) => AUTO_REPLY_FLOWS.find((f) => f.key === k)?.title || k)}
              />
            </Box>
          ) : null}
        </Box>
      </Box>}
    </Stack>
  );
};

export default HubAutomationsTab;
